import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import * as z from 'zod';

import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { iconsPointsOfInterest } from 'src/features/parkingManagement/utils/pointsOfInterestTableColumns';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';

import type { UseFormReturn } from 'react-hook-form';
import type { ParkingPointsOfInterest } from 'src/features/parkingManagement/types/parking';

export type CreatePointOfInterestFormSchemaValues = z.infer<typeof createPointOfInterestFormSchema>;
const createPointOfInterestFormSchema = z.object({
    ids: z.array(z.string()),
});

interface PointOfInterestFormProps {
    form: UseFormReturn<CreatePointOfInterestFormSchemaValues>;
    onSubmit: () => void;
    listOfPointsOfInterest: ParkingPointsOfInterest[];
}

const PointOfInterestForm = ({ form, onSubmit, listOfPointsOfInterest }: PointOfInterestFormProps) => {
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="ids"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    const fieldData = field.value
                        .map((point) => listOfPointsOfInterest?.find((p) => p.id === point)?.name)
                        .join(', ');
                    const allValues = listOfPointsOfInterest.map((point) => point.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...parkingManagementMessages.pointsOfInterest} />
                            </FormLabel>
                            <FormControl>
                                <MultiSelect {...rest}>
                                    <MultiSelectTrigger asChild>
                                        <MultiSelectControl className="w-full">
                                            {fieldData}
                                            {fieldData.length == 0 && (
                                                <span className="text-gray-400">
                                                    <FormattedMessage {...parkingManagementMessages.pointsOfInterest} />
                                                </span>
                                            )}
                                        </MultiSelectControl>
                                    </MultiSelectTrigger>
                                    <MultiSelectContent allValues={allValues}>
                                        {listOfPointsOfInterest.map((point) => (
                                            <MultiSelectOption key={point.id} selected={point.id}>
                                                <div className="flex flex-row justify-between">
                                                    <FontAwesomeIcon
                                                        icon={iconsPointsOfInterest[point.type]}
                                                        className="text-primary-600 mr-4"
                                                    />
                                                    {point.name}
                                                </div>
                                            </MultiSelectOption>
                                        ))}
                                    </MultiSelectContent>
                                </MultiSelect>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
};

export default PointOfInterestForm;
